import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  ScrollText,
  Loader2,
  Copy,
  Check,
  ExternalLink,
  Terminal,
  RefreshCw,
  Calendar,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { api } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import type { ImmudbStream } from '@/components/projects/ImmudbSection'
import { AuditSdkSnippets } from '@/components/projects/AuditSdkSnippets'

export const Route = createFileRoute('/projects/audit/$projectId')({
  component: ProjectAuditPage,
})

interface ProjectSummary {
  id: number
  nom: string
}

interface AuditEntryLine {
  key: string
  line: string
  metadata?: Record<string, unknown>
  value?: unknown
  namespace_key?: string | null
}

interface AuditEntriesResponse {
  count: number
  entries: AuditEntryLine[]
  next_cursor?: string | null
  has_more?: boolean
}

/** Logs are fetched in batches of this size (immudb serves max 1000 keys per scan). */
const LOG_BATCH_SIZE = 1000

function entryIdentity(entry: AuditEntryLine): string {
  return entry.namespace_key || entry.key
}

type DatePreset = 'today' | '7d' | '30d' | 'custom' | 'all'

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function endOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

function toInputDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Best-effort timestamp from metadata or log-* key. */
function entryLevel(entry: AuditEntryLine): 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | 'OTHER' {
  const raw = String(entry.metadata?.level || entry.line.trim().split(/\s+/)[0] || '').toUpperCase()
  if (raw.startsWith('WARN')) return 'WARN'
  if (raw.startsWith('ERR') || raw.startsWith('CRIT') || raw.startsWith('FATAL')) return 'ERROR'
  if (raw.startsWith('DEBUG') || raw.startsWith('TRACE')) return 'DEBUG'
  if (raw.startsWith('INFO')) return 'INFO'
  return 'OTHER'
}

function entryTimestamp(entry: AuditEntryLine): Date | null {
  const meta = entry.metadata || {}
  const raw = meta.time ?? meta.timestamp ?? meta.ts
  if (typeof raw === 'string' || typeof raw === 'number') {
    const d = new Date(raw)
    if (!Number.isNaN(d.getTime())) return d
  }
  const m = /^log-(\d{10,13})-/.exec(entry.key || '')
  if (m) {
    const n = Number(m[1])
    const ms = n < 1e12 ? n * 1000 : n
    const d = new Date(ms)
    if (!Number.isNaN(d.getTime())) return d
  }
  return null
}

function ProjectAuditPage() {
  const { projectId } = Route.useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  const terminalRef = useRef<HTMLDivElement>(null)
  const stickTop = useRef(true)

  const [project, setProject] = useState<ProjectSummary | null>(null)
  const [stream, setStream] = useState<ImmudbStream | null>(null)
  const [lines, setLines] = useState<AuditEntryLine[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingStream, setLoadingStream] = useState(true)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  // Cursor of the last entry received: polling only asks for what comes after it.
  const cursorRef = useRef<string | null>(null)
  const fetchingRef = useRef(false)
  const [creating, setCreating] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [showConfig, setShowConfig] = useState(false)

  const [preset, setPreset] = useState<DatePreset>('all')
  const [fromDate, setFromDate] = useState(() => toInputDate(new Date()))
  const [toDate, setToDate] = useState(() => toInputDate(new Date()))
  const [levelFilter, setLevelFilter] = useState<'ALL' | 'INFO' | 'WARN' | 'ERROR'>('ALL')
  const [logsError, setLogsError] = useState<string | null>(null)

  useEffect(() => {
    if (!isAdmin) {
      navigate({ to: '/projects/$projectId', params: { projectId } })
    }
  }, [isAdmin, navigate, projectId])

  useEffect(() => {
    const load = async () => {
      if (!projectId) return
      setLoading(true)
      try {
        const response = await api.get<ProjectSummary>(`/projects/${projectId}`)
        setProject(response.data)
      } catch (error) {
        console.error('Erreur chargement projet:', error)
        navigate({ to: '/projects' })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [projectId, navigate])

  const appendLines = useCallback((incoming: AuditEntryLine[]) => {
    if (incoming.length === 0) return
    setLines((prev) => {
      const seen = new Set(prev.map(entryIdentity))
      const fresh = incoming.filter((entry) => !seen.has(entryIdentity(entry)))
      return fresh.length ? [...prev, ...fresh] : prev
    })
  }, [])

  const fetchBatch = useCallback(
    async (after: string | null) => {
      const params: Record<string, string> = { limit: String(LOG_BATCH_SIZE) }
      if (after) params.after = after
      const response = await api.get<AuditEntriesResponse>(
        `/projects/${projectId}/immudb/entries`,
        params,
      )
      const data = response.data
      const entries = data?.entries || []
      if (data?.next_cursor) cursorRef.current = data.next_cursor
      return { entries, hasMore: Boolean(data?.has_more) && entries.length > 0 }
    },
    [projectId],
  )

  /**
   * Load logs batch by batch.
   * - full reload (default): resets the cursor, shows the first batch as soon as
   *   it arrives, then keeps appending the next ones.
   * - silent (polling): only fetches entries after the last known cursor.
   */
  const loadLogs = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!projectId || !isAdmin) return
      if (fetchingRef.current) return
      const silent = Boolean(opts?.silent)
      fetchingRef.current = true
      if (!silent) {
        setLoadingLogs(true)
        cursorRef.current = null
        setLines([])
      }
      let received = 0
      try {
        let hasMore = true
        while (hasMore) {
          const batch = await fetchBatch(cursorRef.current)
          appendLines(batch.entries)
          received += batch.entries.length
          hasMore = batch.hasMore
          if (!silent) {
            setLoadingLogs(false)
            setLoadingMore(hasMore)
          }
        }
        setLogsError(null)
      } catch (error) {
        console.error('Erreur chargement logs:', error)
        // Keep whatever batches already arrived; only report when nothing could be shown.
        if (!silent && received === 0) {
          setLogsError('Impossible de charger les logs (API immudb injoignable ou 502).')
          setLines([])
        }
      } finally {
        fetchingRef.current = false
        if (!silent) {
          setLoadingLogs(false)
          setLoadingMore(false)
        }
      }
    },
    [projectId, isAdmin, fetchBatch, appendLines],
  )

  useEffect(() => {
    const loadStream = async () => {
      if (!projectId || !isAdmin) return
      setLoadingStream(true)
      try {
        const response = await api.get<ImmudbStream | null>(`/projects/${projectId}/immudb`)
        setStream(response.data ?? null)
      } catch (error) {
        console.error('Erreur chargement journal:', error)
        setStream(null)
      } finally {
        setLoadingStream(false)
      }
    }
    loadStream()
  }, [projectId, isAdmin])

  useEffect(() => {
    if (stream) {
      loadLogs()
    } else {
      setLines([])
    }
  }, [stream, loadLogs])

  useEffect(() => {
    if (!stream || !isAdmin) return
    const id = window.setInterval(() => {
      void loadLogs({ silent: true })
    }, 3000)
    return () => window.clearInterval(id)
  }, [stream, isAdmin, loadLogs])

  const dateRange = useMemo(() => {
    const now = new Date()
    if (preset === 'all') return null
    if (preset === 'today') {
      return { from: startOfDay(now), to: endOfDay(now) }
    }
    if (preset === '7d') {
      const from = startOfDay(now)
      from.setDate(from.getDate() - 6)
      return { from, to: endOfDay(now) }
    }
    if (preset === '30d') {
      const from = startOfDay(now)
      from.setDate(from.getDate() - 29)
      return { from, to: endOfDay(now) }
    }
    // custom
    const from = fromDate ? startOfDay(new Date(fromDate + 'T00:00:00')) : null
    const to = toDate ? endOfDay(new Date(toDate + 'T00:00:00')) : null
    if (!from && !to) return null
    return { from: from || new Date(0), to: to || endOfDay(now) }
  }, [preset, fromDate, toDate])

  const filteredLines = useMemo(() => {
    const matched = lines.filter((entry) => {
      if (levelFilter !== 'ALL' && entryLevel(entry) !== levelFilter) return false
      if (!dateRange) return true
      const ts = entryTimestamp(entry)
      if (!ts) return false
      return ts >= dateRange.from && ts <= dateRange.to
    })
    return matched.sort((a, b) => {
      const ta = entryTimestamp(a)?.getTime() ?? 0
      const tb = entryTimestamp(b)?.getTime() ?? 0
      return tb - ta
    })
  }, [lines, dateRange, levelFilter])

  useEffect(() => {
    const el = terminalRef.current
    if (!el || !stickTop.current) return
    el.scrollTop = 0
  }, [filteredLines])

  const handleCreate = async () => {
    if (!projectId) return
    setCreating(true)
    try {
      const response = await api.post<ImmudbStream>(`/projects/${projectId}/immudb`)
      setStream(response.data)
      setShowConfig(true)
    } catch (error) {
      console.error('Erreur activation journal:', error)
      alert("Impossible d’activer le journal d’audit. Vérifie la config IMMUDB côté serveur.")
    } finally {
      setCreating(false)
    }
  }

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(label)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      /* ignore */
    }
  }

  const lineClass = (line: string) => {
    if (line.startsWith('ERROR') || line.includes(' ERROR ')) return 'text-red-400'
    if (line.startsWith('WARN') || line.includes(' WARN ')) return 'text-amber-300'
    if (line.startsWith('DEBUG')) return 'text-slate-400'
    return 'text-emerald-300/90'
  }

  const presets: { id: DatePreset; label: string }[] = [
    { id: 'today', label: "Aujourd'hui" },
    { id: '7d', label: '7 jours' },
    { id: '30d', label: '30 jours' },
    { id: 'custom', label: 'Intervalle' },
    { id: 'all', label: 'Tout' },
  ]

  if (!isAdmin) return null
  if (loading) return <PageSkeleton />

  return (
    <div className="min-h-[100dvh] flex flex-col bg-gradient-to-b from-slate-50 to-white">
      <main className="flex-1 flex flex-col w-full max-w-[1600px] mx-auto px-4 sm:px-6 py-4 sm:py-5 gap-4 min-h-0">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 shrink-0">
          <div>
            <button
              type="button"
              onClick={() => navigate({ to: '/projects/$projectId', params: { projectId } })}
              className="mb-2 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au projet
            </button>
            <div className="flex items-center gap-2 text-cyan-600 mb-1">
              <Terminal className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Journal d’audit</span>
              {stream ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live · 3s
                </span>
              ) : null}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
              {project?.nom || 'Projet'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {filteredLines.length}
              {filteredLines.length !== lines.length ? ` / ${lines.length}` : ''} log(s)
              {' · plus récent en haut'}
              {loadingMore ? ' · chargement des lots suivants…' : ''}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => loadLogs()}
              disabled={!stream || loadingLogs}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingLogs ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowConfig((v) => !v)}>
              <ScrollText className="w-4 h-4 mr-2" />
              {showConfig ? 'Masquer la config' : 'SDK & clés'}
            </Button>
            {!stream && !loadingStream && (
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              >
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Activation…
                  </>
                ) : (
                  'Activer le journal'
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        {stream ? (
          <div className="shrink-0 rounded-2xl border border-gray-200/80 bg-white/90 backdrop-blur px-3 sm:px-4 py-3 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
                <Calendar className="w-3.5 h-3.5" />
                Période
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {presets.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPreset(p.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      preset === p.id
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {preset === 'custom' ? (
                  <>
                    <label className="text-[11px] text-gray-400 uppercase tracking-wide">Du</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800"
                    />
                    <label className="text-[11px] text-gray-400 uppercase tracking-wide">Au</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800"
                    />
                  </>
                ) : null}
                <div className="flex items-center gap-1 ml-auto">
                  {(['ALL', 'INFO', 'WARN', 'ERROR'] as const).map((lvl) => (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setLevelFilter(lvl)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-mono font-semibold transition-colors ${
                        levelFilter === lvl
                          ? lvl === 'ERROR'
                            ? 'bg-red-500 text-white'
                            : lvl === 'WARN'
                              ? 'bg-amber-500 text-white'
                              : lvl === 'INFO'
                                ? 'bg-emerald-600 text-white'
                                : 'bg-slate-800 text-white'
                          : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                      }`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {showConfig && (
          <div className="shrink-0 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            {loadingStream ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement config…
              </div>
            ) : !stream ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <p className="text-sm text-gray-600">
                  Active le journal pour générer l’URL d’envoi et la clé d’accès.
                </p>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white shrink-0"
                >
                  {creating ? 'Activation…' : 'Activer le journal'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Journal actif
                  {stream.immudb_project_name ? ` · ${stream.immudb_project_name}` : ''}
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">URL</p>
                    <div className="flex gap-2">
                      <input
                        readOnly
                        value={stream.entries_url}
                        className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-mono text-gray-700"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => copy('url', stream.entries_url)}
                      >
                        {copied === 'url' ? (
                          <Check className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Clé</p>
                    <div className="flex gap-2">
                      <input
                        readOnly
                        value={stream.api_token}
                        className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-mono text-gray-700"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => copy('token', stream.api_token)}
                      >
                        {copied === 'token' ? (
                          <Check className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                <AuditSdkSnippets
                  baseUrl={stream.base_url}
                  token={stream.api_token}
                  entriesUrl={stream.entries_url}
                />
                <a
                  href={`${stream.base_url}/docs`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-cyan-600 hover:text-cyan-700"
                >
                  <ExternalLink className="h-3 w-3" />
                  Docs API
                </a>
              </div>
            )}
          </div>
        )}

        {/* Terminal — full remaining viewport */}
        <div className="flex-1 min-h-[28rem] rounded-2xl border border-slate-800/80 bg-[#0b0f14] shadow-2xl shadow-slate-900/20 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-[#12181f] shrink-0">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
            <span className="ml-3 text-xs font-mono text-slate-500 truncate">
              audit@{project?.nom || 'project'}
              {stream ? ` · ${filteredLines.length} affiché(s)` : ' · journal inactif'}
            </span>
            {stream ? (
              <span className="ml-auto text-[10px] font-mono text-emerald-500/80 hidden sm:inline">
                ● live
              </span>
            ) : null}
          </div>
          <div
            ref={terminalRef}
            onScroll={(e) => {
              stickTop.current = e.currentTarget.scrollTop < 80
            }}
            className="flex-1 overflow-auto p-4 sm:p-5 font-mono text-[12px] sm:text-[13px] leading-6 bg-[#0b0f14] min-h-0"
          >
            {loadingLogs ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement des logs…
              </div>
            ) : !stream ? (
              <p className="text-slate-500">
                # aucun journal actif — clique sur « Activer le journal » pour commencer
              </p>
            ) : logsError ? (
              <p className="text-rose-400/90"># {logsError}</p>
            ) : lines.length === 0 ? (
              <p className="text-slate-500">
                # journal vide — envoie un log via le SDK ou Actualiser
              </p>
            ) : filteredLines.length === 0 ? (
              <p className="text-slate-500">
                # {lines.length} log(s) masqué(s) par le filtre — clique « Tout » ou ALL
              </p>
            ) : (
              <>
                {filteredLines.map((entry, i) => {
                  const ts = entryTimestamp(entry)
                  return (
                    <div
                      key={`${entry.key}-${i}`}
                      className={`${lineClass(entry.line)} whitespace-pre-wrap break-all hover:bg-white/[0.03] -mx-2 px-2 rounded`}
                    >
                      <span className="text-slate-600 select-none mr-2">
                        {String(i + 1).padStart(3, '0')}
                      </span>
                      {ts ? (
                        <span className="text-slate-500 mr-2 select-none">
                          {ts.toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          })}
                        </span>
                      ) : null}
                      {entry.line}
                    </div>
                  )
                })}
                <div className="mt-3 text-cyan-400/80">
                  <span className="animate-pulse">▌</span>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
